import { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import {
  OrdenTrabajoQueryKeysEnum,
  useFetchUbicacionProductoSimple,
  useUpdateAgendamiento,
} from "@/shared/actions/erp";
import { useAuthStore } from "@/store/auth";
import ConceptosTrasladoSection from "@/erp/orden-servicio/orden-servicio-traslado/components/otros-conceptos-traslado/ConceptosTrasladoSection";
import ExcedenteFibraTraslado from "@/erp/orden-servicio/orden-servicio-traslado/components/otros-conceptos-traslado/ExcedenteFibraTraslado";
import { useExcedenteFibraLive } from "@/erp/orden-servicio/orden-servicio-traslado/components/otros-conceptos-traslado/excedente-fibra.actions";
import {
  aprobacionDe,
  cambiaronItems,
  conceptosPendientesAprobacion,
  marcarAprobado,
  nombreUsuario,
  trazaOtrosConceptos,
  trazaPuntosRed,
} from "@/erp/orden-servicio/orden-servicio-traslado/components/otros-conceptos-traslado/conceptos-traslado.utils";
import type { ConceptoVisita } from "@/erp/orden-servicio/orden-servicio-visita/components/otros-conceptos/OtrosConceptosVisita";
import type { PuntoRed } from "@/erp/orden-servicio/orden-servicio-visita/components/otros-conceptos/PuntosDeRedVisita";

type Props = {
  ordenTrabajo: any;
  readOnly?: boolean;
};

/**
 * Card "Otros conceptos del traslado" para el operador de ACTIVACIONES
 * (Pendientes / Recoordinadas / Gestionadas). Es independiente del formulario
 * de instalaciones (`SaveActivacionInstallPendingOT`): edita y APRUEBA los
 * conceptos/puntos de red del traslado directamente en el agendamiento.
 *
 * Regla: cuando el técnico cambia conceptos con el traslado ya iniciado, quedan
 * PENDIENTES; aquí se aprueban (o se corrigen y aprueban) para que el técnico
 * pueda finalizar.
 *
 * También muestra el "Excedente de fibra" que registra el técnico: si supera los
 * metros gratis, Activaciones lo valida, define precio y forma de pago y lo
 * aprueba (bloquea la finalización del técnico hasta entonces).
 */
export default function ConceptosTrasladoActivacionesCard({ ordenTrabajo, readOnly = false }: Props) {
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();
  const obs: any = ordenTrabajo?.agendamiento_data?.observaciones || {};
  const agendamientoId = ordenTrabajo?.agendamiento_data?.id_agendamiento || ordenTrabajo?.agendamiento;

  const [conceptos, setConceptos] = useState<ConceptoVisita[]>([]);
  const [puntosRed, setPuntosRed] = useState<PuntoRed[]>([]);
  const syncKey = JSON.stringify({ oc: obs?.otros_conceptos || [], pr: obs?.puntos_red || [] });
  useEffect(() => {
    setConceptos(Array.isArray(obs?.otros_conceptos) ? obs.otros_conceptos : []);
    setPuntosRed(Array.isArray(obs?.puntos_red) ? obs.puntos_red : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncKey]);

  // Excedente de fibra EN VIVO: Activaciones ve lo que registra el técnico sin
  // recargar. Se aprueba con su propio botón/endpoint (independiente de los
  // conceptos), con precio y forma de pago.
  const { excedente: excedenteFibra } = useExcedenteFibraLive(ordenTrabajo);

  const dirty =
    cambiaronItems(obs?.otros_conceptos, conceptos) || cambiaronItems(obs?.puntos_red, puntosRed);
  const pendiente = conceptosPendientesAprobacion(obs);
  const aprobacion = aprobacionDe(obs);

  // Stock por producto en la ubicación de la flota (referencia, no bloquea).
  const ubicacionFlota = ordenTrabajo?.flota_data?.ubicacion;
  const { data: invStock } = useFetchUbicacionProductoSimple({
    enabled: !!ubicacionFlota,
    params: {
      page: 1,
      page_size: 500,
      estado: true,
      filterByState: false,
      es_para_venta: true,
      ubicacion: ubicacionFlota,
    } as any,
  });
  const stockPorProducto = useMemo(() => {
    const m: Record<string, number> = {};
    const arr = (invStock as any)?.data?.data || [];
    for (const up of arr) {
      const idp = up?.producto_data?.id_producto || up?.id;
      if (idp) m[idp] = Number(up?.cantidad ?? 0);
    }
    return m;
  }, [invStock]);

  const updateAgendamiento = useUpdateAgendamiento<any>({
    enableRedirect: false,
    enableSuccessAlert: false,
    enableErrorAlert: false,
  });

  const guardarYAprobar = async () => {
    if (!agendamientoId) {
      toast.error("No se pudo resolver el agendamiento del traslado.");
      return;
    }
    const usuario = nombreUsuario(user);
    try {
      await updateAgendamiento.mutateAsync({
        id: agendamientoId,
        data: {
          observaciones: {
            ...obs,
            otros_conceptos: conceptos,
            otros_conceptos_historial: trazaOtrosConceptos(
              obs?.otros_conceptos,
              conceptos,
              obs?.otros_conceptos_historial,
              usuario,
              "activaciones"
            ),
            puntos_red: puntosRed,
            puntos_red_historial: trazaPuntosRed(
              obs?.puntos_red,
              puntosRed,
              obs?.puntos_red_historial,
              usuario,
              "activaciones"
            ),
            // Activaciones es quien aprueba: al guardar desde aquí queda APROBADO.
            conceptos_aprobacion:
              conceptos.length > 0 || puntosRed.length > 0 || pendiente
                ? marcarAprobado(aprobacion, usuario)
                : obs?.conceptos_aprobacion || null,
          },
        },
      });
      toast.success(pendiente ? "Conceptos del traslado aprobados." : "Conceptos del traslado guardados.");
      // Refresca la OT (trae agendamiento_data.observaciones actualizado).
      await queryClient.invalidateQueries({
        queryKey: [OrdenTrabajoQueryKeysEnum.OrdenTrabajo, String(ordenTrabajo?.id_orden_trabajo)],
      });
    } catch (e) {
      console.error("No se pudo guardar/aprobar los conceptos del traslado", e);
      toast.error("No se pudo guardar los conceptos del traslado.");
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <ConceptosTrasladoSection
        conceptos={conceptos}
        onChangeConceptos={setConceptos}
        puntosRed={puntosRed}
        onChangePuntosRed={setPuntosRed}
        readOnly={readOnly}
        ubicacionFlota={ubicacionFlota}
        stockPorProducto={stockPorProducto}
        aprobacion={aprobacion}
        despuesDePuntosRed={
          <ExcedenteFibraTraslado
            idOrdenTrabajo={ordenTrabajo?.id_orden_trabajo}
            excedente={excedenteFibra}
            modo="activaciones"
            bloqueado={readOnly}
          />
        }
        subtitle={
          pendiente
            ? `El técnico ${aprobacion?.solicitado_por ? `(${aprobacion.solicitado_por}) ` : ""}agregó o cambió conceptos con el traslado ya iniciado. Revísalos, corrige si hace falta y apruébalos para que pueda finalizar.`
            : "Conceptos/equipos cobrados en el traslado (catálogo de traslados). Puedes ajustarlos; al guardar quedan aprobados."
        }
        footer={
          !readOnly && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {!dirty && !pendiente && (conceptos.length > 0 || puntosRed.length > 0) && (
                <Typography variant="caption" color="success.main">
                  ✓ Guardado y aprobado
                </Typography>
              )}
              <LoadingButton
                variant="contained"
                color={pendiente ? "warning" : "secondary"}
                loading={updateAgendamiento.isPending}
                disabled={!dirty && !pendiente}
                onClick={guardarYAprobar}
              >
                {pendiente ? "Aprobar conceptos del técnico" : "Guardar conceptos"}
              </LoadingButton>
            </Box>
          )
        }
      />
    </Box>
  );
}
